import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { teacherRequestsApi } from "@/api";
import type { TeacherRequestFilter, TeacherRequestStatus } from "@/api";
import { QUERY_KEYS } from "@/constants";

// ─── O'quvchi ─────────────────────────────────────────────────────────────────

/** O'z arizalarim (holatlari bilan). */
export const useMyTeacherRequests = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MY_TEACHER_REQUESTS],
    queryFn: () => teacherRequestsApi.getMine(),
    enabled,
    select: res => res.data,
  });

/** O'qituvchilik arizasini yuboradi (multipart forma). */
export const useApplyTeacherRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormData) => teacherRequestsApi.apply(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_TEACHER_REQUESTS] });
    },
  });
};

// ─── Moderator / admin ──────────────────────────────────────────────────────

export const useTeacherRequests = (filter: TeacherRequestFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_TEACHER_REQUESTS, filter],
    queryFn: () => teacherRequestsApi.list(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/** Navbat tablari uchun sonlar. */
export const TEACHER_REQUEST_STATUSES: readonly TeacherRequestStatus[] = [
  "pending",
  "approved",
  "rejected",
];

/**
 * Har bir holat bo'yicha ariza soni.
 *
 * Backend'da alohida "stats" endpoint'i YO'Q, shuning uchun sonlar
 * `limit: 1` bilan uchta ro'yxat so'rovining `meta.total` idan olinadi —
 * bu bir sahifalik yuk, lekin tab yorliqlari haqiqiy sonni ko'rsatadi.
 * Alohida endpoint qo'shilsa, shu hook o'rniga o'sha ulanadi.
 */
export const useTeacherRequestCounts = () => {
  const queries = useQueries({
    queries: TEACHER_REQUEST_STATUSES.map(status => ({
      queryKey: [QUERY_KEYS.GET_TEACHER_REQUESTS, "count", status],
      queryFn: () => teacherRequestsApi.list({ page: 1, limit: 1, status }),
      select: (res: Awaited<ReturnType<typeof teacherRequestsApi.list>>) => res.meta.total,
    })),
  });

  const counts = {
    pending: queries[0]?.data,
    approved: queries[1]?.data,
    rejected: queries[2]?.data,
  };

  return {
    counts,
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

export const useTeacherRequest = (id: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_TEACHER_REQUEST, id],
    queryFn: () => teacherRequestsApi.getOne(id as number),
    enabled: !!id,
    select: res => res.data,
  });

const useInvalidateTeacherRequests = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TEACHER_REQUESTS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TEACHER_REQUEST] });
  };
};

export const useApproveTeacherRequest = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateTeacherRequests();
  return useMutation({
    mutationFn: (id: number) => teacherRequestsApi.approve(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("TEACHER_REQUEST.APPROVED_TOAST"));
    },
  });
};

export const useRejectTeacherRequest = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateTeacherRequests();
  return useMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      teacherRequestsApi.reject(vars.id, vars.reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("TEACHER_REQUEST.REJECTED_TOAST"));
    },
  });
};
